import { isEmpty, isNumber } from "lodash-es";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { getJobRequestClientInfo, IJobRequest } from "@/data/job-requests";
import { Col, Flex, Row } from "@/eworkitComponents/components";
import { useFormats } from "@/hooks/Formats";
import { SessionContext } from "@/infrastructure/Session";
import { JobRequestDetailSection } from "@/views/JobRequests/Detail/components/JobRequestDetailSection";

interface IJobRequestDetailSectionsProps {
  payload: IJobRequest;
}

const ClientRateType = {
  HOURLY: "Hourly",
  FIXED: "Fixed",
};

export const JobRequestAdditionalDetailSections: FC<IJobRequestDetailSectionsProps> = ({ payload }) => {
  const { t } = useTranslation("session");
  const { formatNumber, formatDate, formatRate } = useFormats();
  const { authenticated } = useContext(SessionContext);

  const rateEmpty = isEmpty(payload.rate) || !payload.rate.maxRate;

  const { name } = getJobRequestClientInfo(payload);

  return (
    <Flex direction="column">
      <Row>
        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailSection label={t("session:JOB_REQUESTS.DETAIL.CLIENT")} value={name} />
        </Col>

        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailSection
            label={t("session:JOB_REQUESTS.DETAIL.PUBLISHED_DATE")}
            value={formatDate(payload.firstDayOfApplications)}
          />
        </Col>

        {!rateEmpty && authenticated && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.SALARY_RANGE")}
              value={`${t("session:JOB_REQUESTS.DETAIL.UP_TO")} ${formatRate(
                payload.rate.maxRate,
                payload.rate.currency,
                payload.rate.clientRateType === "HOURLY" ? "h" : undefined,
              )}`}
              grow={0}
            />
          </Col>
        )}
        {isNumber(payload.numberOfPositions) && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.NUMBER_OF_POSITIONS")}
              value={formatNumber(payload.numberOfPositions)}
              grow={0}
            />
          </Col>
        )}

        {!!payload.businessUnitName && authenticated && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.BUSINESS_UNIT")}
              value={payload.businessUnitName}
              grow={0}
            />
          </Col>
        )}

        {!!payload.legalEntityName && authenticated && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.LEGAL_ENTITY")}
              value={payload.legalEntityName}
              grow={0}
            />
          </Col>
        )}

        {!!payload.legalEntityVatNo && authenticated && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.LEGAL_ENTITY_VAT")}
              value={payload.legalEntityVatNo}
              grow={0}
            />
          </Col>
        )}
        {isNumber(payload.hoursPerWeek) && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.HOURS_PER_WEEK")}
              value={payload.hoursPerWeek}
              grow={0}
            />
          </Col>
        )}
        {payload.rate.clientRateType && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailSection
              label={t("session:JOB_REQUESTS.DETAIL.RATE_TYPE")}
              value={ClientRateType[payload.rate.clientRateType]}
              grow={0}
            />
          </Col>
        )}
      </Row>
    </Flex>
  );
};
